// Pure wording and layout helpers for the App journey module (UX design 2026-10-28-app-journey-ux-design.md).
// Every value shown comes from the journey API; nothing here derives a figure (I-4).

import type { Drift, Flow, Flows, Graph, GraphEdge, GraphNode, Step } from "./api";

export const FLOW = "flow";
export const SCREEN = "screen";
export const CATALOGUE_FILE = "flow-strips-library.json";

export const NO_RELEASE_TEXT = "No production capture yet. The first release capture creates it.";
export const SEQUENCE_ERROR_TITLE = "Could not load the sequence.";
export const MAP_ERROR_TITLE = "Could not load the map.";
export const NO_SCREENS_TEXT = `No screens captured for this release. Add a path to ${CATALOGUE_FILE} and it is captured at the next release.`;
export const NO_FRAME = "(no canvas frame)";

/** "Way in 2 · Front door, signed out" from the API; the key when the catalogue gives no title. */
export function flowLabel(key: string, flow: Flow | undefined): string {
  return flow?.title || key;
}

/** "Goal" from "goal", "Store available" from "store_available": the variable names are the catalogue's. */
export function variableLabel(name: string): string {
  const words = name.replace(/_/g, " ").trim();
  return words ? words[0].toUpperCase() + words.slice(1) : name;
}

/**
 * The flow to open on: the URL's when the release has it, else Way in 2 (UX F1: the most used
 * flow), else the first flow the API lists.
 */
export function pickFlow(flows: Flows | undefined, requested: string | null): string | null {
  if (!flows) return null;
  const keys = Object.keys(flows.flows);
  if (requested && keys.includes(requested)) return requested;
  if (keys.includes("way_in_2")) return "way_in_2";
  return keys[0] ?? null;
}

/** Defaults: the first captured path's answers, so the arrival paint is a real sequence, never a 404. */
export function defaultVars(flow: Flow | undefined): Record<string, string> {
  const first = flow?.captured.find((p) => p.ok) ?? flow?.captured[0];
  return first ? { ...first.vars } : {};
}

/**
 * The answers to send. Unless the URL names this very flow (none picked yet, or a stale link whose
 * flow this release no longer has) they are the defaults; after that, exactly the flow's variables
 * the URL sets to an allowed value, so "Not asked" on every field stays a real choice. An unset variable is left out (the API matches a captured path's
 * answers exactly, and a path that never asks a question does not carry it).
 */
export function chosenVars(key: string | null, flow: Flow | undefined, url: Record<string, unknown>): Record<string, string> {
  if (!flow) return {};
  const names = Object.keys(flow.variables);
  if (url[FLOW] !== key) return defaultVars(flow);
  const out: Record<string, string> = {};
  for (const name of names) {
    const value = url[name];
    if (typeof value === "string" && flow.variables[name].includes(value)) out[name] = value;
  }
  return out;
}

/** "A2d · Collagen add-on"; a page with no frame reads "(no canvas frame) <page>" (dev spec invariant). */
export function caption(step: Pick<Step, "frame_id" | "frame_title" | "page" | "route">, withheld = false): string {
  if (withheld) return step.page ?? step.route;
  if (!step.frame_id) return `${NO_FRAME} ${step.page ?? step.route}`;
  return step.frame_title ? `${step.frame_id} · ${step.frame_title}` : step.frame_id;
}

export function tapLine(tap: string | null): string {
  return tap ? `Tap: ${tap}` : "end";
}

export function releaseLine(release: string, canvasVersion: string | null | undefined): string {
  return canvasVersion ? `Production release ${release} · canvas v${canvasVersion}` : `Production release ${release}`;
}

/** Short hashes for the header (H8 names the commit and bundle on every screen). */
export function shortHash(value: string | null | undefined): string {
  return value ? value.slice(0, 8) : "–";
}

export function driftLines(drift: Drift[]): string[] {
  return drift.map((d) => {
    if (d.kind === "canvas_version") return `canvas moved: v${d.captured} → v${d.published}`;
    if (d.kind === "pairing_gaps") return `frame map has ${d.gaps.length} unpaired frame${d.gaps.length === 1 ? "" : "s"}: labels withheld`;
    if (d.kind === "version_off") return "the capture's canvas version did not match its frame map";
    return "published canvas version unknown";
  });
}

export function notCapturedText(release: string, pending: string | null | undefined): string {
  const head = `Not captured for release ${release}.`;
  if (pending) return `${head} This path waits on ${pending}.`;
  return `${head} Add this path to ${CATALOGUE_FILE} and it is captured at the next release.`;
}

export type Placed = GraphNode & { column: number; row: number };

/**
 * Columns by distance from an entry screen (a node nothing leads to), so a way in reads left to
 * right with no overlap (UX §4 Map edge: 60 nodes). Order inside a column follows the API's order.
 */
export function layoutGraph(graph: Pick<Graph, "nodes" | "edges">): Placed[] {
  const incoming = new Map<string, number>();
  for (const e of graph.edges) if (e.from !== e.to) incoming.set(e.to, (incoming.get(e.to) ?? 0) + 1);
  const column = new Map<string, number>();
  const queue: string[] = [];
  for (const n of graph.nodes) {
    if (!incoming.has(n.route)) {
      column.set(n.route, 0);
      queue.push(n.route);
    }
  }
  while (queue.length) {
    const route = queue.shift() as string;
    const next = (column.get(route) ?? 0) + 1;
    for (const e of graph.edges) {
      if (e.from === route && !column.has(e.to)) {
        column.set(e.to, next);
        queue.push(e.to);
      }
    }
  }
  const rows = new Map<number, number>();
  return graph.nodes.map((n) => {
    // A screen only reachable through a loop has no entry above it: it goes in the first column.
    const c = column.get(n.route) ?? 0;
    const r = rows.get(c) ?? 0;
    rows.set(c, r + 1);
    return { ...n, column: c, row: r };
  });
}

export function neighbours(edges: GraphEdge[], route: string) {
  return {
    before: edges.filter((e) => e.to === route && e.from !== route),
    after: edges.filter((e) => e.from === route && e.to !== route),
  };
}

/** A captured path that walks through `route`, so a map node can open a sequence that shows it. */
export function pathThrough(edges: GraphEdge[], route: string): string | null {
  const edge = edges.find((e) => e.from === route || e.to === route);
  return edge?.paths[0] ?? null;
}
